import { message } from "antd";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { type ItemInterface } from "react-sortablejs";

import { cn } from "@btnet/utilities-core";

import { BdsButton, BdsContentHeader } from "@buildertrend/components";

import { showApixErrorMessage } from "utilities/api/apix-handler";

import { type ExportHeaderResponse } from "commonComponents/btWrappers/BTSocialMediaButtons/FileShare.types";
import { Frame } from "commonComponents/btWrappers/Frame/Frame";
import GenerateQRCodeModal from "commonComponents/entity/sharing/GenerateQRCodeModal";
import { DragAndDrop } from "commonComponents/utilities/DragAndDrop/DragAndDrop";

import { type PlansShareResponse } from "handlers";

import { type PlanPage } from "handlers/models/planPage";
import { type PlanSet } from "handlers/models/planSet";
import { plansSharePostV2, usePlansReorderPagesPutV2 } from "handlers/plans/plans";

import { PlansCard } from "entity/Plans/PlansCard/PlansCard";
import { type SharePlanType } from "entity/Plans/PlansList/PlansList";

import "./PlansOrganizer.less";

interface IPlansOrganizerProps {
    planPages: PlanPage[];
    planSet: PlanSet;
    jobId: number;
    jobName?: string;
    onSave: () => void;
    onCancel: () => void;
    onDeletePage?: (pageId: string) => Promise<void>;
    refetch: () => void;
}

type DraggablePlanPage = PlanPage & ItemInterface;

const PlansGridContainer = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    (props, ref) => {
        const { className, ...rest } = props;
        return (
            <div ref={ref} className={cn("plans-grid", className)} {...rest}>
                {props.children}
            </div>
        );
    },
);
PlansGridContainer.displayName = "PlansGridContainer";

export const PlansOrganizer = ({
    planPages,
    planSet,
    jobId,
    jobName,
    onSave,
    onCancel,
    onDeletePage,
    refetch,
}: IPlansOrganizerProps) => {
    const originalOrderRef = useRef(planPages.map((p) => p.pageId).join(","));

    const [localPages, setLocalPages] = useState<DraggablePlanPage[]>(() =>
        planPages.map((p) => ({ ...p, id: p.pageId })),
    );

    useEffect(() => {
        const incomingOrder = planPages.map((p) => p.pageId).join(",");
        if (incomingOrder !== originalOrderRef.current) {
            originalOrderRef.current = incomingOrder;
            setLocalPages(planPages.map((p) => ({ ...p, id: p.pageId })));
        }
    }, [planPages]);

    const [isSaving, setIsSaving] = useState(false);
    const [showGenerateQRCodeModal, setShowGenerateQRCodeModal] = useState<boolean>(false);
    const [shareData, setShareData] = useState<PlansShareResponse | null>(null);
    const [planPageTitle, setPlanPageTitle] = useState<string | null>(null);

    const currentOrder = localPages.map((p) => p.pageId).join(",");
    const hasChanges = currentOrder !== originalOrderRef.current;

    const { mutateAsync: reorderPages } = usePlansReorderPagesPutV2();

    const planPageRenamelisteners = useRef<Record<string, () => void>>({});
    const setEditPlanPageNameListener = useCallback((id: string, callback: () => void) => {
        planPageRenamelisteners.current[id] = callback;
    }, []);

    const handleDeletePage = useCallback(
        async (pageId: string) => {
            if (onDeletePage) {
                await onDeletePage(pageId);
                originalOrderRef.current = originalOrderRef.current
                    .split(",")
                    .filter((id) => id !== pageId)
                    .join(",");
                setLocalPages((prev) => prev.filter((p) => p.pageId !== pageId));
            }
        },
        [onDeletePage],
    );

    const handleSave = useCallback(async () => {
        setIsSaving(true);
        try {
            await reorderPages({
                data: {
                    setId: planSet.setId,
                    pageIdsInOrder: localPages.map((p) => p.pageId),
                },
                params: { jobId },
            });
            onSave();
        } catch (error) {
            showApixErrorMessage(error as Error, "Failed to reorder pages");
        } finally {
            setIsSaving(false);
        }
    }, [reorderPages, planSet.setId, localPages, jobId, onSave]);

    const onCopySharePageLink = useCallback(
        async (pageId?: string, _shareType?: SharePlanType) => {
            // Safari/iOS require navigator.clipboard.write to run inside the
            // user-gesture frame; see PlansList.handleCopyShareLink.
            const sharePromise = plansSharePostV2({ jobId: jobId, pageId: pageId });
            try {
                const clipboardItem = new ClipboardItem({
                    "text/plain": sharePromise.then(
                        (resp) => new Blob([resp.data.shareableLink ?? ""], { type: "text/plain" }),
                    ),
                });
                await navigator.clipboard.write([clipboardItem]);
                const resp = await sharePromise;
                if (resp.data.shareableLink) {
                    void message.success("Share link copied");
                }
            } catch {
                const resp = await sharePromise;
                if (resp.data.shareableLink) {
                    await navigator.clipboard.writeText(resp.data.shareableLink);
                    void message.success("Share link copied");
                }
            }
        },
        [jobId],
    );

    const onGenerateQrCodePage = useCallback(
        async (_shareType: SharePlanType, pageId?: string, qrCodeTitle?: string) => {
            const resp = await plansSharePostV2({ jobId: jobId, pageId });
            if (resp.data.shareableLink) {
                if (qrCodeTitle) {
                    setPlanPageTitle(qrCodeTitle);
                }
                setShareData(resp.data);
                setShowGenerateQRCodeModal(true);
            }
        },
        [jobId],
    );

    const generateQrCodeModalConfig = {
        beforeClose: () => {
            setPlanPageTitle(null);
            setShowGenerateQRCodeModal(false);
        },
        parentRoute: location.pathname,
    };

    const qrCodeModal = (
        <GenerateQRCodeModal
            exportHeader={shareData?.exportHeaderResponse as ExportHeaderResponse}
            shareUrl={shareData?.shareableLink}
            title={planPageTitle ?? "Plans"}
            modalConfig={generateQrCodeModalConfig}
            bottomDescriptionMarkup={
                <>
                    Use your phone's camera to open in Buildertrend
                    <br />
                    Anyone can view this file
                </>
            }
        />
    );

    return (
        <>
            <Frame className="landscape-scroll plans-organizer" data-testid="plans-organizer">
                <BdsContentHeader
                    id="plans-organizer-header"
                    testid="plans-organizer-header"
                    title={planSet.name}
                    jobName={jobName}
                    slotTop={
                        <div className="organizer-actions">
                            <BdsButton
                                testid="plans-organizer-cancel"
                                displayType="secondary"
                                onClick={onCancel}
                                text="Cancel"
                            />
                            <BdsButton
                                testid="plans-organizer-save"
                                displayType="primary"
                                disabled={!hasChanges}
                                loading={isSaving}
                                onClick={handleSave}
                                text="Save"
                            />
                        </div>
                    }
                />

                <DragAndDrop
                    className="flex-1 overflow-auto max-w-2xl mx-auto"
                    list={localPages}
                    setList={setLocalPages}
                    tag={PlansGridContainer}
                    animation={200}
                    fallbackOnBody
                >
                    {localPages.map((plan) => (
                        <PlansCard
                            key={plan.pageId}
                            plan={plan}
                            isDragEnabled
                            jobId={jobId}
                            refetch={refetch}
                            setEditPlanPageNameListener={setEditPlanPageNameListener}
                            planPageRenamelisteners={planPageRenamelisteners}
                            onDeletePage={handleDeletePage}
                            onCopySharePageLink={onCopySharePageLink}
                            onGenerateQrCodePage={onGenerateQrCodePage}
                        />
                    ))}
                </DragAndDrop>
            </Frame>
            {showGenerateQRCodeModal && qrCodeModal}
        </>
    );
};
